import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sparkles, RefreshCw, AlertTriangle, CheckCircle2, ExternalLink,
  TrendingUp, ShieldAlert, Target, FileText,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Contract } from '../types';
import { transformContract, SELECT_COLUMNS } from '../hooks/useContracts';
import toast from 'react-hot-toast';

// One AI match row joined with its (transformed) contract.
interface AiMatch {
  contract_id: string;
  fit_score: number;
  tier: 'strong' | 'possible' | 'weak' | 'ineligible' | string;
  eligibility: string | null;
  rationale: string | null;
  blockers: string[];
  opportunities: string[];
  recommended_action: string | null;
  scored_at: string;
  contract?: Contract;
}

interface AiBestMatchesProps {
  onSelectContract: (contract: Contract) => void;
  onGenerateProposal: (contract: Contract) => void;
}

type TierFilter = 'all' | 'strong' | 'possible' | 'weak';

const TIER_STYLES: Record<string, { ring: string; badge: string; label: string }> = {
  strong: { ring: 'border-green-300', badge: 'bg-success-bg text-success', label: 'Strong fit' },
  possible: { ring: 'border-blue-200', badge: 'bg-bw-primary-tint text-bw-primary', label: 'Possible' },
  weak: { ring: 'border-bw-border', badge: 'bg-bw-surface-subtle text-bw-ink', label: 'Weak' },
  ineligible: { ring: 'border-red-200', badge: 'bg-urgent-bg text-urgent', label: 'Ineligible' },
};

function scoreColor(score: number): string {
  if (score >= 75) return 'text-success';
  if (score >= 50) return 'text-bw-primary';
  if (score >= 25) return 'text-fit-moderate';
  return 'text-bw-muted';
}

function formatValue(c?: Contract): string | null {
  const v = c?.totalContractValue || c?.awardAmount || c?.fundingAmount;
  if (!v) return null;
  return `$${Number(v).toLocaleString()}`;
}

function daysLeft(c?: Contract): string | null {
  if (!c?.dueDate) return null;
  const d = Math.ceil((new Date(c.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (d < 0) return 'Closed';
  if (d === 0) return 'Due today';
  return `${d} day${d === 1 ? '' : 's'} left`;
}

export function AiBestMatches({ onSelectContract, onGenerateProposal }: AiBestMatchesProps) {
  const { user } = useAuth();
  const [matches, setMatches] = useState<AiMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState<{ scored: number; remaining: number } | null>(null);
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');
  // True when the matcher is turned off (admin toggle / kill switch). Rendered
  // as a calm inline notice instead of a red "scan failed" error.
  const [disabled, setDisabled] = useState(false);

  const loadMatches = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // 1. AI match rows (RLS limits to this user), best first.
      const { data: rows, error } = await supabase
        .from('ai_contract_matches')
        .select('contract_id, fit_score, tier, eligibility, rationale, blockers, opportunities, recommended_action, scored_at')
        .eq('user_id', user.id)
        .order('fit_score', { ascending: false })
        .limit(300);
      if (error) throw error;

      const aiRows = (rows || []) as AiMatch[];
      const ids = aiRows.map(r => r.contract_id);

      // 2. Hydrate the underlying contracts so cards can open the existing
      //    slideout / proposal flow unchanged.
      const contractMap = new Map<string, Contract>();
      // .in() with a large list is fine here (<=300 ids).
      if (ids.length > 0) {
        const { data: contractsData } = await supabase
          .from('sam_contracts')
          .select(SELECT_COLUMNS)
          .in('notice_id', ids);
        (contractsData || []).forEach((rec: any) => {
          contractMap.set(rec.notice_id, transformContract(rec));
        });
      }

      setMatches(aiRows.map(r => ({ ...r, contract: contractMap.get(r.contract_id) })));
    } catch (e) {
      console.error('Failed to load AI matches:', e);
      toast.error('Could not load AI matches');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadMatches(); }, [loadMatches]);

  // Manual, single-batch scan. Each click scores ONE bounded batch of open
  // solicitations and then stops — there is no client auto-loop and the edge
  // function no longer self-chains. This keeps Anthropic (Haiku) token spend
  // explicit and user-controlled; click again to score the next batch.
  const runScan = useCallback(async (force: boolean) => {
    if (!user || scanning) return;
    setScanning(true);
    setDisabled(false);
    setProgress({ scored: 0, remaining: 0 });

    const invokeOnce = async (acknowledge: boolean) => {
      const { data, error } = await supabase.functions.invoke('ai-match-contracts', {
        body: {
          user_id: user.id,
          max_per_run: 36,
          batch_size: 12,
          force,
          auto_continue: false,
          acknowledge_budget: acknowledge,
        },
      });
      if (error) throw error;
      if (data && data.success === false) throw new Error(data.error || 'Scan failed');
      return data;
    };

    try {
      let data = await invokeOnce(false);

      if (data?.disabled) {
        setDisabled(true);
        toast('AI matching is currently turned off by an administrator.', { icon: 'ℹ️' });
        await loadMatches();
        return;
      }

      // Daily spend cap: when reached, require explicit acknowledgement before
      // spending another window. Repeats for each subsequent window.
      while (data?.budget_exceeded && data?.requires_ack) {
        await loadMatches();
        const ok = window.confirm(
          `${data.message || 'Daily AI matching budget reached.'}\n\n` +
          `Spent today: $${Number(data.spent ?? 0).toFixed(2)} ` +
          `(current limit $${Number(data.cap ?? 0).toFixed(2)}).\n\n` +
          `Click OK to allow another $${Number(data.daily_cap ?? 0.75).toFixed(2)} of AI matching ` +
          `and continue, or Cancel to stop for now.`
        );
        if (!ok) {
          toast('AI matching paused at the daily limit.', { icon: 'ℹ️' });
          await loadMatches();
          return;
        }
        data = await invokeOnce(true);
        if (data?.disabled) {
          setDisabled(true);
          toast('AI matching is currently turned off by an administrator.', { icon: 'ℹ️' });
          await loadMatches();
          return;
        }
      }

      const scored = data?.scored || 0;
      const remaining = data?.remaining ?? 0;
      setProgress({ scored, remaining });
      toast.success(
        remaining > 0
          ? `Scored ${scored} solicitations — ${remaining} not yet scored. Click again to score the next batch.`
          : `Scored ${scored} solicitations — all open solicitations evaluated.`
      );
      // Some sub-batches errored but others succeeded — surface it rather than
      // silently under-reporting (they'll be retried on the next scan).
      if (data?.failed_batches > 0) {
        toast(`${data.failed_batches} batch(es) couldn't be scored and will retry on the next scan.`,
          { icon: '⚠️' });
      }
      await loadMatches();
    } catch (e: any) {
      console.error('AI scan error:', e);
      const msg: string = e?.message || '';
      toast.error(
        /ANTHROPIC|not configured/i.test(msg)
          ? 'AI matching is not configured (missing ANTHROPIC_API_KEY).'
          : msg
            ? `AI scan failed: ${msg}`
            : 'AI scan failed. Partial results may be shown.'
      );
      await loadMatches();
    } finally {
      setScanning(false);
      setProgress(null);
    }
  }, [user, scanning, loadMatches]);

  const counts = useMemo(() => {
    const c = { all: matches.length, strong: 0, possible: 0, weak: 0 };
    for (const m of matches) {
      if (m.tier === 'strong') c.strong++;
      else if (m.tier === 'possible') c.possible++;
      else if (m.tier === 'weak') c.weak++;
    }
    return c;
  }, [matches]);

  const visible = useMemo(() => {
    if (tierFilter === 'all') return matches.filter(m => m.tier !== 'ineligible');
    return matches.filter(m => m.tier === tierFilter);
  }, [matches, tierFilter]);

  const hasResults = matches.length > 0;

  return (
    <div>
      {/* Header / control bar */}
      <div className="bg-bw-primary rounded-xl p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="h-6 w-6" /> AI Best Matches
            </h2>
            <p className="mt-1 text-indigo-100 text-sm max-w-2xl">
              An AI capture analyst (Claude) reads every open solicitation against your
              company profile — judging set-aside eligibility, real capability fit and
              strategic value — and ranks the best ones to pursue. This goes beyond the
              keyword-based BidWin score.
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-2 min-w-[220px]">
            <button
              onClick={() => runScan(false)}
              disabled={scanning}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-bw-surface text-bw-primary font-semibold rounded-lg hover:bg-bw-primary-tint disabled:opacity-60 transition-colors"
            >
              {scanning
                ? <><RefreshCw className="h-4 w-4 animate-spin" /> Scanning…</>
                : <><Sparkles className="h-4 w-4" /> {hasResults ? 'Scan for new matches' : 'Find my best matches'}</>}
            </button>
            {hasResults && (
              <button
                onClick={() => runScan(true)}
                disabled={scanning}
                className="text-xs text-indigo-100 hover:text-white underline disabled:opacity-60"
              >
                Re-score everything from scratch
              </button>
            )}
          </div>
        </div>

        {scanning && progress && (
          <div className="mt-4 bg-white/15 rounded-lg px-4 py-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Evaluated {progress.scored} so far…</span>
              <span className="text-indigo-100">{progress.remaining} remaining</span>
            </div>
            <div className="mt-2 w-full bg-white/25 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-bw-surface h-1.5 rounded-full transition-all duration-500"
                style={{
                  width: `${progress.scored + progress.remaining > 0
                    ? Math.round((progress.scored / (progress.scored + progress.remaining)) * 100)
                    : 5}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* AI matching turned off notice (admin toggle / kill switch) */}
      {disabled && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-fit-moderate-border bg-fit-moderate-bg p-4">
          <AlertTriangle className="h-5 w-5 text-fit-moderate mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-bw-ink">AI matching is turned off</p>
            <p className="text-bw-muted mt-0.5">
              AI Best Matches is currently disabled, so no new scoring will run. Any
              previously scored matches are still shown below. An administrator can turn
              it back on in the admin console under <strong>Settings → Feature Toggles</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Tier filter chips */}
      {hasResults && (
        <div className="flex flex-wrap gap-2 mb-5">
          {([
            ['all', `All (${counts.all})`],
            ['strong', `Strong (${counts.strong})`],
            ['possible', `Possible (${counts.possible})`],
            ['weak', `Weak (${counts.weak})`],
          ] as [TierFilter, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTierFilter(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                tierFilter === key
                  ? 'bg-bw-primary text-white border-indigo-600'
                  : 'bg-bw-surface text-bw-ink border-bw-border hover:border-indigo-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="text-center py-16 text-bw-muted">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-3" /> Loading matches…
        </div>
      ) : !hasResults ? (
        <div className="text-center py-16 bg-bw-surface rounded-xl border border-dashed border-bw-border">
          <Sparkles className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
          <p className="text-bw-ink font-medium">No AI matches yet</p>
          <p className="text-bw-muted text-sm mt-1 max-w-md mx-auto">
            Click <strong>Find my best matches</strong> and the AI will scan every open
            solicitation and rank the strongest opportunities for your business. This can
            take a few minutes the first time.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((m) => {
            const style = TIER_STYLES[m.tier] || TIER_STYLES.weak;
            const c = m.contract;
            const value = formatValue(c);
            const due = daysLeft(c);
            const samUrl = c?.uiLink || c?.solicitationUrl || c?.additionalInfoLink;
            return (
              <div
                key={m.contract_id}
                className={`bg-bw-surface rounded-xl border ${style.ring} shadow-sm hover:shadow-card transition-shadow p-5`}
              >
                <div className="flex items-start gap-4">
                  {/* Score dial */}
                  <div className="flex flex-col items-center justify-center min-w-[64px]">
                    <span className={`text-3xl font-bold ${scoreColor(m.fit_score)}`}>{m.fit_score}</span>
                    <span className="text-[10px] uppercase tracking-wide text-bw-muted">fit</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.badge}`}>
                        {style.label}
                      </span>
                      {m.eligibility && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          m.eligibility === 'eligible' ? 'bg-success-bg text-success'
                          : m.eligibility === 'ineligible' ? 'bg-urgent-bg text-urgent'
                          : 'bg-fit-moderate-bg text-fit-moderate'
                        }`}>
                          {m.eligibility}
                        </span>
                      )}
                      {due && <span className="text-xs text-bw-muted">{due}</span>}
                      {value && <span className="text-xs text-bw-muted">· {value}</span>}
                    </div>

                    <h3
                      className="font-semibold text-bw-ink leading-snug cursor-pointer hover:text-bw-primary"
                      onClick={() => c && onSelectContract(c)}
                    >
                      {c?.title || m.contract_id}
                    </h3>
                    {c?.agency && <p className="text-sm text-bw-muted mt-0.5">{c.agency}</p>}

                    {m.rationale && (
                      <p className="text-sm text-bw-ink mt-2">{m.rationale}</p>
                    )}

                    {/* Opportunities & blockers */}
                    {(m.opportunities?.length > 0 || m.blockers?.length > 0) && (
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {m.opportunities?.length > 0 && (
                          <div className="text-xs">
                            <div className="flex items-center gap-1 text-success font-medium mb-1">
                              <TrendingUp className="h-3.5 w-3.5" /> Why it fits
                            </div>
                            <ul className="space-y-0.5 text-bw-muted">
                              {m.opportunities.slice(0, 3).map((o, i) => (
                                <li key={i} className="flex gap-1"><CheckCircle2 className="h-3 w-3 text-success mt-0.5 shrink-0" />{o}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {m.blockers?.length > 0 && (
                          <div className="text-xs">
                            <div className="flex items-center gap-1 text-urgent font-medium mb-1">
                              <ShieldAlert className="h-3.5 w-3.5" /> Watch-outs
                            </div>
                            <ul className="space-y-0.5 text-bw-muted">
                              {m.blockers.slice(0, 3).map((b, i) => (
                                <li key={i} className="flex gap-1"><AlertTriangle className="h-3 w-3 text-urgent mt-0.5 shrink-0" />{b}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {m.recommended_action && (
                      <div className="mt-3 flex items-start gap-1.5 text-sm">
                        <Target className="h-4 w-4 text-bw-primary mt-0.5 shrink-0" />
                        <span className="text-bw-ink"><strong>Recommended:</strong> {m.recommended_action}</span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => c && onSelectContract(c)}
                        disabled={!c}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-bw-border rounded-lg text-bw-ink hover:bg-bw-surface-subtle disabled:opacity-50"
                      >
                        <FileText className="h-4 w-4" /> View details
                      </button>
                      <button
                        onClick={() => c && onGenerateProposal(c)}
                        disabled={!c}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-bw-primary text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <Sparkles className="h-4 w-4" /> Generate proposal
                      </button>
                      {samUrl && (
                        <a
                          href={samUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-bw-muted hover:text-bw-primary"
                        >
                          <ExternalLink className="h-4 w-4" /> SAM.gov
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
